/**
 * Chart documentation shown behind the info icon on the Campaigns Charts
 * dashboard.
 *
 * Keys match the chart link property names in `MopaCampaignsChartsComponent`.
 */
export const MopaCampaignsChartsChartDocs = {
    // COHORT PERFORMANCE

    arpu: /* HTML */ `<p>
            Cumulative revenue per user of selected first-open cohorts, by day
            number.
        </p>
        <pre>
  cohort revenue at that day number (numerator)
  ------------------------------
  cohort size with activity through that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative revenue per user, in the selected
            currency.
        </p>
        <p>
            The line can fall when net revenue includes negative corrections;
            the denominator includes cohorts with recorded activity through
            that day. The Revenue type setting picks total, in-app or ad revenue.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code> (revenue),
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    running_roas: /* HTML */ `<p>
            Cumulative return on ad spend by day number.
        </p>
        <pre>
  revenue up to that day number (numerator)
  ------------------------------
  campaign cost (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - revenue recovered against spend, in % - 100% is
            break-even.
        </p>
        <p>
            The line can fall when net revenue includes negative corrections.
            Costs use AppsFlyer reporting dates; missing days do not break the
            line.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code> (revenue, BIEvents;
            valid players only);
            <code>bi_viz.dashboard_campaigns</code> (cost, AppsFlyer).
        </p>`,

    roas_calendar: /* HTML */ `<p>
            Return on ad spend at a fixed day number, by AppsFlyer reporting
            date.
        </p>
        <pre>
  revenue up to that day number (numerator)
  ------------------------------
  campaign cost (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - AppsFlyer reporting date, bucketed by
            <strong>Date aggregation</strong>; Y - revenue recovered against
            spend, in %, one line per day number.
        </p>
        <p>
            One line per day number from the "Calendar ROAS days" setting.
            Missing dates stay as gaps; costs use AppsFlyer reporting dates.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code> (revenue, BIEvents;
            valid players only);
            <code>bi_viz.dashboard_campaigns</code> (cost, AppsFlyer).
        </p>`,

    retention_per_day_no: /* HTML */ `<p>
            Share of a cohort still active at a given day number since install.
        </p>
        <pre>
  players active on that day number (numerator)
  ------------------------------
  players matured to that day number (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - share of the cohort still active, in %.
        </p>
        <p>
            Cohorts too young to have reached that day number are left out of
            both sides rather than counted as churned, so day 0 is 100%.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_retention</code>; valid players only.
        </p>`,

    avg_user_lifetime: /* HTML */ `<p>
            Estimated days a user stays active, read at a given day number - the
            area under the retention curve.
        </p>
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - expected active days.
        </p>
        <p>
            <strong>How it's built:</strong> by-day-no retention rates summed
            from day 0 up to that day number, minus 1. The -1 drops day 0, which
            is 100% for everyone.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_retention</code>; valid players only.
        </p>`,

    // REVENUE AND USERS

    revenue: /* HTML */ `<p>
            Revenue by activity date for selected first-open cohorts.
        </p>
        <p>
            <strong>Axes:</strong> X - activity date, bucketed by
            <strong>Date aggregation</strong>; Y - revenue in the selected
            currency.
        </p>
        <p>
            <strong>What's counted:</strong> daily revenue rows summed over the
            date; no denominator. The Revenue type setting picks total, in-app
            or ad revenue, and net/gross and currency follow their toggles.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code>; valid players only.
        </p>`,

    dau: /* HTML */ `<p>
            Active users by activity date for selected first-open cohorts.
        </p>
        <p>
            <strong>Axes:</strong> X - activity date, bucketed by
            <strong>Date aggregation</strong>; Y - active players, summed into
            player-days above daily aggregation.
        </p>
        <p>
            <strong>What's counted:</strong> distinct <code>player_id</code> per
            day, then summed over the period - under Week or Month aggregation
            this is a sum of daily DAU (player-days), not WAU or MAU.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; valid players
            only.
        </p>`,

    wavg_arpdau: /* HTML */ `<p>
            Revenue per active user by activity date for selected first-open
            cohorts.
        </p>
        <pre>
  revenue (numerator)
  ------------------------------
  sum of daily active users (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - activity date, bucketed by
            <strong>Date aggregation</strong>; Y - revenue per active user, in
            the selected currency.
        </p>
        <p>
            Weighted: sum first, then divide, so under Week or Month aggregation
            the denominator counts player-days, not distinct players.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code> (revenue),
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    wavg_cr: /* HTML */ `<p>
            Share of active players who bought anything by activity date, for
            selected first-open cohorts.
        </p>
        <pre>
  sum of daily payers (numerator)
  ------------------------------
  sum of daily active users (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - activity date, bucketed by
            <strong>Date aggregation</strong>; Y - share of active players who
            bought, in %.
        </p>
        <p>
            Both sides are weighted daily sums, so a player who is active and
            pays on several days counts several times.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code> (payers),
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    new_payers: /* HTML */ `<p>
            Players making their first purchase by activity date, from selected
            first-open cohorts.
        </p>
        <p>
            <strong>Axes:</strong> X - activity date, bucketed by
            <strong>Date aggregation</strong>; Y - players making their first
            purchase.
        </p>
        <p>
            <strong>What's counted:</strong> first-transaction events summed
            over the date. A player has exactly one, so this equals the count of
            brand-new payers; buying again later does not count.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code>; valid players only.
        </p>`,

    new_users: /* HTML */ `<p>
            Players whose first session falls in the selected date or period.
        </p>
        <p>
            <strong>Axes:</strong> X - first-session date, bucketed by
            <strong>Date aggregation</strong>; Y - players.
        </p>
        <p>
            <strong>What's counted:</strong> distinct day-0 players - BI's own
            first-session count, not AppsFlyer installs, so it will not match
            the Installs chart.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; valid players
            only.
        </p>`,

    // CAMPAIGN SPEND

    costs: /* HTML */ `<p>
            Campaign spend by AppsFlyer reporting date.
        </p>
        <p>
            <strong>Axes:</strong> X - AppsFlyer reporting date, bucketed by
            <strong>Date aggregation</strong>; Y - spend in the selected
            currency.
        </p>
        <p>
            <strong>What's counted:</strong> campaign cost summed over the
            reporting date, in the selected currency.
        </p>
        <p>Source: <code>bi_viz.dashboard_campaigns</code>; AppsFlyer.</p>`,

    installs: /* HTML */ `<p>
            Installs attributed by AppsFlyer, by reporting date.
        </p>
        <p>
            <strong>Axes:</strong> X - AppsFlyer reporting date, bucketed by
            <strong>Date aggregation</strong>; Y - attributed installs.
        </p>
        <p>
            <strong>What's counted:</strong> attributed installs summed over the
            date. This is AppsFlyer's count, not BI's first sessions, so it will
            not match the New users chart.
        </p>
        <p>Source: <code>bi_viz.dashboard_campaigns</code>; AppsFlyer.</p>`,

    impressions: /* HTML */ `<p>
            Campaign impressions bought by AppsFlyer reporting date.
        </p>
        <p>
            <strong>Axes:</strong> X - AppsFlyer reporting date, bucketed by
            <strong>Date aggregation</strong>; Y - impressions bought.
        </p>
        <p>
            <strong>What's counted:</strong> impressions summed over the date -
            ads shown to potential players in acquisition campaigns, not ads
            watched inside the game, which the Ads dashboard counts.
        </p>
        <p>Source: <code>bi_viz.dashboard_campaigns</code>; AppsFlyer.</p>`,

    reattributions: /* HTML */ `<p>
            Players re-attributed to a campaign by AppsFlyer reporting date.
        </p>
        <p>
            <strong>Axes:</strong> X - AppsFlyer reporting date, bucketed by
            <strong>Date aggregation</strong>; Y - reattributions.
        </p>
        <p>
            <strong>What's counted:</strong> reattribution events summed over
            the date - returning players credited to a new campaign, not fresh
            installs, so they are not in the Installs chart.
        </p>
        <p>Source: <code>bi_viz.dashboard_campaigns</code>; AppsFlyer.</p>`,

    cpi: /* HTML */ `<p>
            Cost per install by AppsFlyer reporting date.
        </p>
        <pre>
  campaign cost (numerator)
  ------------------------------
  installs (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - AppsFlyer reporting date, bucketed by
            <strong>Date aggregation</strong>; Y - cost per install, in the
            selected currency.
        </p>
        <p>
            Both sides come from AppsFlyer, so player validation does not touch
            this chart.
        </p>
        <p>Source: <code>bi_viz.dashboard_campaigns</code>; AppsFlyer.</p>`,

    cpm: /* HTML */ `<p>
            Cost per thousand campaign impressions by AppsFlyer reporting date.
        </p>
        <pre>
  campaign cost x 1000 (numerator)
  ------------------------------
  impressions (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - AppsFlyer reporting date, bucketed by
            <strong>Date aggregation</strong>; Y - cost per thousand
            impressions, in the selected currency.
        </p>
        <p>
            Both sides come from AppsFlyer. Not to be confused with the Ads
            dashboard's eCPM, which is revenue from ads shown in the game.
        </p>
        <p>Source: <code>bi_viz.dashboard_campaigns</code>; AppsFlyer.</p>`,
} as const;
